import { Component, OnInit, OnDestroy, inject } from '@angular/core';

import { FormsModule, ReactiveFormsModule, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ClarityModule } from '@clr/angular';
import { CodeInputModule, CodeInputComponent } from 'angular-code-input';
import { ToastrService } from 'ngx-toastr';
import { Subject, interval } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import shajs from 'sha.js';

import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import {
  PasswordResetService,
  ValidateTokenResponse,
  VerifyPinResponse,
} from 'src/app/services/Auth/password-reset.service';

/**
 * Reset Password Component
 *
 * Handles the Token + PIN password reset flow:
 * 1. Token validation (from URL query param)
 * 2. PIN entry and verification
 * 3. New password entry and completion
 *
 * Route: /reset-password?token=...
 */
@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  imports: [ClarityModule, FormsModule, ReactiveFormsModule, MrTranslatePipe, CodeInputModule],
  standalone: true,
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private mrTranslate = inject(MrTranslatePipe);
  private passwordResetService = inject(PasswordResetService);

  private destroy$ = new Subject<void>();

  // Flow states
  currentStep: 'loading' | 'invalid-token' | 'enter-pin' | 'enter-password' | 'success' | 'error' = 'loading';

  // Token validation
  token: string | null = null;
  pinHint: string | null = null;
  expiresAt: string | null = null;

  // PIN entry
  pinValue: string = '';
  attemptsRemaining: number | null = null;
  isPinLoading = false;

  // Password entry
  passwordForm = new UntypedFormGroup({
    newPassword: new UntypedFormControl('', [Validators.required, Validators.minLength(8)]),
    confirmPassword: new UntypedFormControl('', [Validators.required]),
  });
  isPasswordLoading = false;
  passwordMismatch = false;

  // Timer for expiration countdown
  timeRemaining: string = '';
  private timerInterval: any;

  // UI
  isDarkThemeAktiv = localStorage.getItem('theme') === 'dark';

  // Password strength indicators
  hasMinLength = false;
  hasUpperCase = false;
  hasLowerCase = false;
  hasNumber = false;
  hasSpecialChar = false;

  ngOnInit(): void {
    // Get token from URL query parameter - use take(1) to prevent multiple validations
    this.route.queryParams.pipe(take(1), takeUntil(this.destroy$)).subscribe(params => {
      this.token = params['token'];
      console.log('🔑 [ResetPassword] Token from URL:', this.token);
      if (this.token) {
        this.validateToken();
      } else {
        this.currentStep = 'invalid-token';
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopTimer();
    this.passwordResetService.resetState();
  }

  /**
   * Step 1: Validate the token from the URL
   */
  private validateToken(): void {
    if (!this.token) return;

    console.log('🔑 [ResetPassword] Starting token validation...');

    this.passwordResetService
      .validateToken(this.token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ValidateTokenResponse) => {
          console.log('🔑 [ResetPassword] Received response:', response);
          console.log('🔑 [ResetPassword] response.valid =', response.valid, 'type:', typeof response.valid);

          if (response.valid) {
            console.log('🔑 [ResetPassword] Token is VALID, showing PIN form');
            this.pinHint = response.pinHint ?? null;
            this.expiresAt = response.expiresAt ?? null;
            this.currentStep = 'enter-pin';
            console.log('🔑 [ResetPassword] currentStep set to:', this.currentStep);

            // Start countdown timer if we have expiration
            if (this.expiresAt) {
              this.startExpirationTimer();
            }
          } else {
            console.log('🔑 [ResetPassword] Token is INVALID');
            this.currentStep = 'invalid-token';
          }
        },
        error: err => {
          console.error('🔑 [ResetPassword] Error in subscription:', err);
          this.currentStep = 'invalid-token';
        },
      });
  }

  /**
   * Step 2: Verify the entered PIN
   */
  verifyPin(): void {
    if (this.pinValue.length !== 6) {
      this.toastr.error(this.mrTranslate.transform('Bitte geben Sie den 6-stelligen PIN ein.'));
      return;
    }

    this.isPinLoading = true;

    this.passwordResetService
      .verifyPin(this.pinValue)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: VerifyPinResponse) => {
          this.isPinLoading = false;

          if (response.verified) {
            this.currentStep = 'enter-password';
            this.stopTimer(); // Stop expiration timer, we now have reset session
          } else {
            this.attemptsRemaining = response.attemptsRemaining ?? null;

            if (this.attemptsRemaining !== null && this.attemptsRemaining <= 0) {
              this.currentStep = 'invalid-token';
              this.toastr.error(
                this.mrTranslate.transform('Zu viele Fehlversuche. Bitte fordern Sie einen neuen Link an.')
              );
            } else {
              const remainingMsg =
                this.attemptsRemaining ?
                  ` (${this.attemptsRemaining} ${this.mrTranslate.transform('Versuche verbleibend')})`
                : '';
              this.toastr.error(this.mrTranslate.transform('Ungültiger PIN.') + remainingMsg);
            }

            // Clear PIN input
            this.pinValue = '';
          }
        },
        error: () => {
          this.isPinLoading = false;
          this.toastr.error(this.mrTranslate.transform('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.'));
        },
      });
  }

  /**
   * Step 3: Complete password reset with new password
   */
  completeReset(): void {
    if (!this.passwordForm.valid) {
      this.toastr.error(this.mrTranslate.transform('Bitte füllen Sie alle Felder aus.'));
      return;
    }

    const newPassword = this.passwordForm.get('newPassword')?.value;
    const confirmPassword = this.passwordForm.get('confirmPassword')?.value;

    if (newPassword !== confirmPassword) {
      this.passwordMismatch = true;
      this.toastr.error(this.mrTranslate.transform('Die Passwörter stimmen nicht überein.'));
      return;
    }

    this.passwordMismatch = false;
    this.isPasswordLoading = true;

    // Hash password with SHA-256 before sending (same as login)
    const passwordHash = shajs('sha256').update(newPassword).digest('hex');

    this.passwordResetService
      .completeReset(passwordHash)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isPasswordLoading = false;

          if (response.success) {
            this.currentStep = 'success';
            this.toastr.success(this.mrTranslate.transform('Ihr Passwort wurde erfolgreich geändert.'));
          } else {
            this.currentStep = 'error';
            this.toastr.error(this.mrTranslate.transform('Das Passwort konnte nicht geändert werden.'));
          }
        },
        error: () => {
          this.isPasswordLoading = false;
          this.currentStep = 'error';
          this.toastr.error(this.mrTranslate.transform('Ein Fehler ist aufgetreten.'));
        },
      });
  }

  /**
   * Handle PIN code input completion
   */
  onPinCodeCompleted(code: string): void {
    this.pinValue = code;
    // Auto-submit when 6 digits entered
    this.verifyPin();
  }

  /**
   * Handle PIN code change
   */
  onPinCodeChanged(code: string): void {
    this.pinValue = code;
  }

  /**
   * Check password strength as user types
   */
  checkPasswordStrength(): void {
    const password = this.passwordForm.get('newPassword')?.value || '';

    this.hasMinLength = password.length >= 8;
    this.hasUpperCase = /[A-Z]/.test(password);
    this.hasLowerCase = /[a-z]/.test(password);
    this.hasNumber = /\d/.test(password);
    this.hasSpecialChar = /[!@#$%^&*(),.?":{}|<>\-_=+\[\]{}\\;:'"<>,./`~]/.test(password);
  }

  /**
   * Check if all password requirements are met
   */
  get isPasswordValid(): boolean {
    return this.hasMinLength && this.hasUpperCase && this.hasLowerCase && this.hasNumber && this.hasSpecialChar;
  }

  /**
   * Navigate to login page
   */
  goToLogin(): void {
    this.router.navigate(['/login']);
  }

  /**
   * Navigate to login page to request new link via modal
   */
  requestNewLink(): void {
    this.router.navigate(['/login']);
  }

  /**
   * Start countdown timer for token expiration
   */
  private startExpirationTimer(): void {
    if (!this.expiresAt) return;

    const updateTimer = () => {
      const now = new Date().getTime();
      // Server sends UTC time - ensure we parse it as UTC by appending 'Z' if not present
      const expiresAtUtc = this.expiresAt!.endsWith('Z') ? this.expiresAt! : this.expiresAt + 'Z';
      const expiry = new Date(expiresAtUtc).getTime();
      const remaining = expiry - now;

      if (remaining <= 0) {
        this.timeRemaining = '00:00';
        this.stopTimer();
        this.currentStep = 'invalid-token';
        return;
      }

      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

      this.timeRemaining = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    updateTimer();
    this.timerInterval = setInterval(updateTimer, 1000);
  }

  /**
   * Stop the expiration timer
   */
  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }
}
